import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { CancelWithConfirm } from './cancel-with-confirm.component';
import { formatShortDate } from './format';

import { AddOnPurchase, AddOnPurchaseStatus } from '@/services/hsm-backend';

export interface AddOnsCardProps {
  addOns: AddOnPurchase[];
  title?: string;
  rightSlot?: React.ReactNode;
  /**
   * When provided, active add-ons get a "Cancelar" action that calls this with
   * the add-on id. Omitted on the read-only dashboard usage.
   */
  onCancelAddOn?: (addOnId: number) => Promise<unknown>;
  /** Called after a successful cancellation (typically the shell's refetch). */
  onCancelled?: () => Promise<void>;
}

type AddOnsView = 'active' | 'all';

const STATUS_LABELS: Record<AddOnPurchaseStatus, string> = {
  active: 'Activo',
  cancelled: 'Cancelado',
  consumed: 'Consumido',
};

const STATUS_COLORS: Record<
  AddOnPurchaseStatus,
  'success' | 'warning' | 'default'
> = {
  active: 'success',
  cancelled: 'warning',
  consumed: 'default',
};

/**
 * Read-only card listing a company's add-on purchases with an
 * "Activos / Todos" toggle. Used both on the Empresa dashboard and as
 * the left card of the Paquete tab. The optional `rightSlot` lets the
 * Empresa tab inject a "Comprar paquete" CTA next to the toggle.
 *
 * @param addOns    Full list of add-on purchases (any status).
 * @param title     Card heading; defaults to "Paquetes (add-ons)".
 * @param rightSlot Extra node rendered to the right of the toggle.
 */
export const AddOnsCard = ({
  addOns,
  title = 'Paquetes (add-ons)',
  rightSlot,
  onCancelAddOn,
  onCancelled,
}: AddOnsCardProps) => {
  const [view, setView] = useState<AddOnsView>('active');

  const visible =
    view === 'active' ? addOns.filter((a) => a.status === 'active') : addOns;

  const handleView = (
    _: React.MouseEvent<HTMLElement>,
    next: AddOnsView | null
  ) => {
    if (next) {
      setView(next);
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">{title}</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={handleView}
              aria-label="Filtro de paquetes"
            >
              <ToggleButton value="active" aria-label="Solo activos">
                Activos
              </ToggleButton>
              <ToggleButton value="all" aria-label="Todos los paquetes">
                Todos
              </ToggleButton>
            </ToggleButtonGroup>
            {rightSlot}
          </Stack>
        </Stack>
        <Divider sx={{ mb: 1, mt: 1 }} />
        {visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {view === 'active'
              ? 'Sin paquetes activos.'
              : 'Sin paquetes registrados.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {visible.map((addOn) => (
              <ListItem
                key={addOn.id}
                disableGutters
                secondaryAction={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={STATUS_LABELS[addOn.status]}
                      color={STATUS_COLORS[addOn.status]}
                    />
                    {onCancelAddOn && addOn.status === 'active' && (
                      <CancelWithConfirm
                        buttonLabel="Cancelar"
                        title="Cancelar paquete"
                        description={`Se cancelará el paquete de ${addOn.messageQty.toLocaleString('es-AR')} mensajes. Los mensajes ya consumidos no se reembolsan.`}
                        confirmLabel="Cancelar paquete"
                        successMessage="Paquete cancelado"
                        errorMessagePrefix="No se pudo cancelar el paquete"
                        onConfirm={() => onCancelAddOn(addOn.id)}
                        onConfirmed={onCancelled}
                      />
                    )}
                  </Stack>
                }
              >
                <ListItemText
                  primary={`${addOn.messageQty.toLocaleString('es-AR')} mensajes`}
                  secondary={
                    <Box component="span">
                      Comprado el {formatShortDate(addOn.createdAt)} · usados{' '}
                      {addOn.usage.toLocaleString('es-AR')}/{addOn.messageQty}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
